<template>
  <view class="list-item" :style="listStyle">
    <template v-for="(item, index) of data" :key="index">
      <view class="item-index">{{ index + 1 }}</view>
      <view class="item-name" :style="nameStyle">{{ item.name }}</view>

      <view class="item-rate-container" flex="dir:right">
        <view class="item-rate" :style="rateClass(item, index)" />
        <view class="item-count" :style="countStyle">{{ item.count }} {{ suffix }}</view>
      </view>
    </template>
  </view>
  <template v-if="shouldScroll">
    <template v-for="item of Array.from({ length: 3 }, (i, k) => k)" :key="item">
      <view class="list-item" :style="listStyle">
        <template v-for="(item, index) of data" :key="index">
          <view class="item-index">{{ index + 1 }}</view>
          <view class="item-name" :style="nameStyle">{{ item.name }}</view>

          <view class="item-rate-container" flex="dir:right">
            <view class="item-rate" :style="rateClass(item, index)" />
            <view class="item-count" :style="countStyle">{{ item.count }} {{ suffix }}</view>
          </view>
        </template>
      </view>
    </template>
  </template>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue"
import { removePx } from "@p-v/utils"
interface ThemeProps {
  data: { name: string; count: number }[]
  itemStyle: { [key: string]: string }
  nameStyle: { [key: string]: string }
  countStyle: { [key: string]: string }
  rateClass: (item: { count: number }, index: number) => { [key: string]: string }
  suffix?: string
  scroll?: boolean
  duration?: number
  minHeight?: number
}
const emits = defineEmits(["update:scroll"])
const props = withDefaults(defineProps<ThemeProps>(), {
  data: () => [],
  itemStyle: () => ({}),
  nameStyle: () => ({}),
  countStyle: () => ({}),
  rateClass: () => ({}),
  suffix: () => "",
  scroll: () => true,
  duration: () => 0,
  minHeight: undefined,
})

const maxHeight = computed(() => {
  let maxHeight = 32
  if (props.nameStyle.height || props.nameStyle.lineHeight) {
    maxHeight = Math.max(maxHeight, Number(removePx(props.nameStyle.lineHeight || props.nameStyle.height)))
  }
  if (props.countStyle.height || props.countStyle.lineHeight) {
    maxHeight = Math.max(maxHeight, Number(removePx(props.countStyle.lineHeight || props.countStyle.height)))
  }
  if (props.itemStyle?.height || props.itemStyle?.lineHeight) {
    maxHeight = Math.max(maxHeight, Number(removePx(props.itemStyle.lineHeight || props.itemStyle.height)))
  }
  return maxHeight
})

const height = computed(() => {
  return props.data.length * maxHeight.value
})

const shouldScroll = ref(props.scroll)

const checkScroll = () => {
  if (height.value > 0 && props.minHeight && height.value <= props.minHeight) {
    shouldScroll.value = false
  } else if (height.value > 0 && props.minHeight && height.value > props.minHeight) {
    shouldScroll.value = true
  } else {
    shouldScroll.value = false
  }
  emits("update:scroll", shouldScroll.value)
}

watch(
  () => height.value,
  () => {
    checkScroll()
  },
  { immediate: true },
)

watch(
  () => props.minHeight,
  () => {
    checkScroll()
  },
  { immediate: true },
)

const listStyle = computed(() => {
  const stlyeObj = {
    display: "grid",
  }

  stlyeObj["grid-template-rows"] = `repeat(${props.data.length}, ${maxHeight.value > 0 ? `${maxHeight.value}px` : "auto"})`
  stlyeObj["grid-template-columns"] = "32px 70px 1fr"
  stlyeObj["height"] = Math.max(height.value, props.minHeight || 0) + "px"
  return { ...stlyeObj, ...props.itemStyle }
})
</script>

<style lang="scss" scoped>
.list-item {
  position: relative;
  height: max-content;
  .item-index {
    font-family:
      Segoe UI,
      Segoe UI;
    font-weight: 400;
    width: 16px;
    text-align: center;
    font-size: 16px;
    color: rgba(230, 247, 255, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    img {
      width: 16px;
      height: 16px;
    }
  }
  .item-name {
    position: relative;
    font-family:
      Segoe UI,
      Segoe UI;
    font-weight: 400;
    font-size: 16px;
    color: #ffffff;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    display: flex;
    align-items: center;
  }
  .item-rate-container {
    position: relative;
    margin-left: 11px;
  }
  .item-count {
    font-family:
      Segoe UI,
      Segoe UI;
    font-weight: 400;
    font-size: 14px;
    color: #ffffff;
    text-shadow: 0 0 3px BLACK;
    position: relative;
    text-align: right;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  .item-rate {
    background: #fff;
    position: absolute;
    top: calc(50% - 4px);
    left: 0;
    width: 0;
    height: 8px;
    background: linear-gradient(90deg, rgba(43, 183, 247, 0.5) 0%, #2dd8ef 100%);
    border-radius: 0px 8px 8px 0px;
  }
  .item-progress-container {
    position: relative;
    width: 100%;
    height: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px 4px 4px 4px;
    margin: auto 0;
    .item-progress {
      display: block;
      height: 8px;
      border-radius: 4px 4px 4px 4px;
      background: linear-gradient(90deg, rgba(56, 112, 212, 0.2) 0%, #2bb7f7 100%);
    }
  }
  .item-suffix {
    font-family:
      Segoe UI,
      Segoe UI;
    font-size: 14px;
    color: #ffffff80;
    text-align: right;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}
</style>
